/**
 * Self-service 2FA card.
 *
 * Three states drive the rendering:
 *   * disabled (default)   — "Set up 2FA" button → setup wizard
 *   * setup wizard         — QR + manual key + verify step + backup codes
 *   * enabled              — status, backup-code count, regenerate, disable
 *
 * The setup wizard advances through three sub-steps:
 *   1. ``scan``    — show QR + copy-paste secret + 6-digit input
 *   2. ``backup``  — display 8 codes once with a "saved" checkbox gate
 *   3. ``done``    — collapses back into the enabled state
 *
 * Backup codes are displayed exactly once at the end of setup (the
 * server only ever returns plaintext codes during /2fa/verify and
 * /2fa/regenerate-backup-codes — they're hashed at rest). Users
 * who lose them must regenerate, which invalidates the old set.
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import {
  disable2FA,
  get2FAStatus,
  regenerate2FABackupCodes,
  setup2FA,
  verify2FA,
  type TwoFactorSetupResponse,
  type TwoFactorStatus,
} from "@/api/twoFactor";

type SetupStep = "scan" | "backup" | "done";

export function TwoFactorCard({ className }: { className?: string }) {
  const { addToast } = useToast();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStep, setSetupStep] = useState<SetupStep>("scan");
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(
    null,
  );
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");

  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setStatus(await get2FAStatus());
    } catch {
      // Quiet fail — user just sees the disabled state.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const data = await setup2FA();
      setSetupData(data);
      setSetupStep("scan");
      setSavedConfirmed(false);
      setCode("");
      setSetupOpen(true);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Couldn't start setup");
    } finally {
      setBusy(false);
    }
  };

  const verifySetup = async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      addToast("error", "Enter the 6-digit code from your app");
      return;
    }
    setBusy(true);
    try {
      const res = await verify2FA(code.trim());
      setBackupCodes(res.backup_codes);
      setSetupStep("backup");
      addToast("success", "Two-factor authentication enabled");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const finishSetup = async () => {
    setSetupOpen(false);
    setSetupStep("done");
    setSetupData(null);
    setBackupCodes([]);
    setSavedConfirmed(false);
    setCode("");
    await refresh();
    await refreshUser();
  };

  const submitDisable = async () => {
    if (!disablePassword) return;
    setBusy(true);
    try {
      await disable2FA(disablePassword);
      addToast("success", "Two-factor authentication disabled");
      setDisableOpen(false);
      setDisablePassword("");
      await refresh();
      await refreshUser();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Couldn't disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const submitRegen = async () => {
    if (!regenPassword) return;
    setBusy(true);
    try {
      const res = await regenerate2FABackupCodes(regenPassword);
      setRegenCodes(res.backup_codes);
      setRegenPassword("");
      addToast("success", "New backup codes generated");
      await refresh();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't regenerate codes",
      );
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = (codes: string[]) => {
    navigator.clipboard
      .writeText(codes.join("\n"))
      .then(() => addToast("success", "Backup codes copied"))
      .catch(() => addToast("error", "Couldn't copy"));
  };

  const enabled = !!status?.enabled;
  const remainingBackup = status?.backup_codes_remaining ?? 0;

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-accent" />
              Two-Factor Authentication
            </span>
          </CardTitle>
          {enabled && (
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <CheckCircle2 className="h-4 w-4" />
              Enabled
            </span>
          )}
        </CardHeader>

        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-text-muted mx-auto my-3" />
        ) : enabled ? (
          <div>
            <p className="text-sm text-text-muted">
              You&rsquo;ll be asked for a 6-digit code from your authenticator
              app every time you sign in.{" "}
              <strong className="text-text-primary">
                {remainingBackup} backup code
                {remainingBackup === 1 ? "" : "s"} remaining
              </strong>
              {remainingBackup < 3 && (
                <span className="ml-1 text-warning inline-flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  consider regenerating
                </span>
              )}
              .
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setRegenOpen(true)}
              >
                <RefreshCw className="h-4 w-4" />
                Regenerate backup codes
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="text-danger border-danger/40"
                onClick={() => setDisableOpen(true)}
              >
                Disable 2FA
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-text-muted">
              Add an extra layer of security to your account. You&rsquo;ll
              use Google Authenticator, Authy, or any TOTP app to generate
              a 6-digit code at sign-in.
            </p>
            <Button
              size="sm"
              className="mt-3"
              onClick={startSetup}
              isLoading={busy}
            >
              <KeyRound className="h-4 w-4" />
              Set up 2FA
            </Button>
          </div>
        )}
      </Card>

      {/* ── Setup wizard ─────────────────────────────────────── */}
      <Modal
        isOpen={setupOpen}
        onClose={() => (busy ? null : setSetupOpen(false))}
        title={
          setupStep === "scan"
            ? "Set up two-factor authentication"
            : setupStep === "backup"
              ? "Save your backup codes"
              : ""
        }
        size="lg"
      >
        {setupStep === "scan" && setupData && (
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Scan this QR code with Google Authenticator, Authy, or any
              TOTP app. Then enter the 6-digit code below to verify.
            </p>

            <div className="flex flex-col items-center gap-3">
              {setupData.qr_png_base64 ? (
                <img
                  src={`data:image/png;base64,${setupData.qr_png_base64}`}
                  alt="2FA QR code"
                  className="h-44 w-44 bg-white p-2 rounded-md"
                />
              ) : (
                <div className="text-xs text-text-muted text-center max-w-sm">
                  Couldn&rsquo;t render the QR. Tap your authenticator
                  app&rsquo;s &ldquo;Enter setup key manually&rdquo; option
                  and use the secret below.
                </div>
              )}

              <div className="w-full">
                <p className="text-[11px] uppercase tracking-wide text-text-muted mb-1">
                  Or enter manually
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-2 py-1.5 rounded border border-border bg-surface text-xs break-all">
                    {setupData.secret}
                  </code>
                  <button
                    onClick={() =>
                      navigator.clipboard
                        .writeText(setupData.secret)
                        .then(() => addToast("success", "Copied"))
                        .catch(() => null)
                    }
                    className="p-1.5 rounded hover:bg-surface-hover text-text-muted"
                    title="Copy"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <Input
              label="6-digit code"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setSetupOpen(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                onClick={verifySetup}
                isLoading={busy}
                disabled={code.length !== 6}
              >
                Verify
              </Button>
            </div>
          </div>
        )}

        {setupStep === "backup" && (
          <div className="space-y-4">
            <div className="rounded-md bg-warning/10 border border-warning/30 px-3 py-2 text-xs text-text-primary">
              <strong>Save these somewhere safe.</strong> Each code can
              only be used once and you won&rsquo;t be shown them again.
              If you lose access to your authenticator app and your
              backup codes, you&rsquo;ll need to contact your administrator.
            </div>

            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((c) => (
                <code
                  key={c}
                  className="px-2 py-1.5 rounded border border-border bg-surface text-sm font-mono text-center"
                >
                  {c}
                </code>
              ))}
            </div>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => copyCodes(backupCodes)}
            >
              <Copy className="h-4 w-4" />
              Copy all
            </Button>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={savedConfirmed}
                onChange={(e) => setSavedConfirmed(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-accent"
              />
              I have saved my backup codes
            </label>

            <div className="flex justify-end">
              <Button onClick={finishSetup} disabled={!savedConfirmed}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Disable confirmation ─────────────────────────────── */}
      <Modal
        isOpen={disableOpen}
        onClose={() => (busy ? null : setDisableOpen(false))}
        title="Disable 2FA?"
      >
        <p className="text-sm text-text-muted">
          You&rsquo;ll only need your password to sign in &mdash; no
          authenticator app required. Confirm with your current password
          to proceed.
        </p>
        <Input
          className="mt-3"
          type="password"
          label="Current password"
          value={disablePassword}
          onChange={(e) => setDisablePassword(e.target.value)}
          autoComplete="current-password"
        />
        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => setDisableOpen(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={submitDisable}
            isLoading={busy}
            disabled={!disablePassword}
          >
            Disable 2FA
          </Button>
        </div>
      </Modal>

      {/* ── Regenerate backup codes ──────────────────────────── */}
      <Modal
        isOpen={regenOpen}
        onClose={() => {
          if (busy) return;
          setRegenOpen(false);
          setRegenCodes(null);
          setRegenPassword("");
        }}
        title={regenCodes ? "New backup codes" : "Regenerate backup codes?"}
      >
        {!regenCodes && (
          <>
            <p className="text-sm text-text-muted">
              Generates a fresh set of 8 codes. <strong>Your old codes
              stop working immediately.</strong> Confirm with your
              password.
            </p>
            <Input
              className="mt-3"
              type="password"
              label="Current password"
              value={regenPassword}
              onChange={(e) => setRegenPassword(e.target.value)}
              autoComplete="current-password"
            />
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setRegenOpen(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                onClick={submitRegen}
                isLoading={busy}
                disabled={!regenPassword}
              >
                Regenerate
              </Button>
            </div>
          </>
        )}

        {regenCodes && (
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Save these — each can only be used once.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {regenCodes.map((c) => (
                <code
                  key={c}
                  className="px-2 py-1.5 rounded border border-border bg-surface text-sm font-mono text-center"
                >
                  {c}
                </code>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => copyCodes(regenCodes)}
              >
                <Copy className="h-4 w-4" />
                Copy all
              </Button>
              <Button
                onClick={() => {
                  setRegenOpen(false);
                  setRegenCodes(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default TwoFactorCard;
